// 唤起app
import env from './env';
import { isAppExist, onVisibilityChange as appOnVisibilityChange } from 'root/common/nativeApi';
import { jumpLocation, wait } from 'root/common/util/index';
import { getAppVersion, compareVersion } from 'root/common/util/getAppVersion';

/**
 * 延迟打开，保证埋点有被上报
 */
function gotoPage(url, isReplace) {
    wait(300).then(() => {
        jumpLocation(url, isReplace);
    });
}

/**
 * 不允许调起第三方app的环境
 */
const blockedEnv = ['isWechat'];

/**
 * 拼接拼多多app唤起链接
 * @param {string} url
 */
export function PDDAppUrl(url) {
    return `pddopen://?appKey=d1d02af3381e48798c8d448b30876e05&packageId=com.black.unique&backUrl=blackunique%3A%2F%2F&h5Url=${encodeURIComponent(
        url
    )}&browser_open=1`;
}

/**
 * 兼容h5的页面显示状态监听
 * 返回off函数
 * @param {Function} listener
 */
export function onVisibilityChange(listener) {
    if (env.isApp) {
        return appOnVisibilityChange(listener);
    } else {
        const listen = function() {
            listener(document.visibilityState === 'visible');
        };
        window.addEventListener('visibilitychange', listen);
        return () => window.removeEventListener('visibilitychange', listen);
    }
}

/**
 * 以await的方式监听页面切回前台
 */
export function waitPageShow() {
    return new Promise(res => {
        const off = onVisibilityChange(isShow => {
            if (isShow) {
                res();
                off();
            }
        });
    });
}

/**
 * 通过URL Scheme的方式唤起app
 * ios有可能不能针对新需求的app正常使用base_phoneIsExistApp检测是否存在
 * 所以通过直接尝试唤起，并监听页面显示状态，如果规定时间内页面被隐藏，说明唤起成功；
 * 否则，唤起失败，直接跳对方h5
 * @param {string} url
 * @param {string} h5url
 * @param {number} timeout
 * @param {boolean} isReplace
 */
export function tryCallApp(url, h5url, timeout, isReplace) {
    return new Promise(res => {
        window.location.href = url;
        // 如果3秒内无法唤起app，跳转到h5
        const timeId = setTimeout(() => {
            off();
            if (h5url) gotoPage(h5url, isReplace);
            res(false);
        }, timeout);
        // 监听页面状态，如果3秒内被隐藏，说明唤醒了app，就清除定时器
        const off = onVisibilityChange(isShow => {
            if (!isShow) clearTimeout(timeId);
            off();
            res(true);
        });
    });
}

/**
 * 尝试唤起第三方app，app下如果有androidAppName或者iosAppName，会检测是否安装对应app
 * 否则将尝试唤起，并监听页面显示状态，如果规定时间内页面被隐藏，说明唤起成功；
 * 否则，唤起失败，直接跳对方h5
 * @param {string} url Scheme URL
 * @param {string} h5url
 * @param {Object} opt
 * @param {number} opt.timeout 等待唤起的时间
 * @param {string} opt.androidAppName 安卓端appName
 * @param {string} opt.iosAppName ios端appName
 * @param {string} miniConf
 * @param {string} miniConf.cb 小程序callback
 * @param {string} miniConf.loadingTxt 小程序跳转中文案
 * @param {string} miniConf.sourceId 小程序原始id
 * @param {string} miniConf.path 小程序路径
 * @returns {String} 返回打开类型(app/小程序/h5)
 */
export default async function callApp(url, h5url, opt = {}, miniConf) {
    const { timeout, androidAppName, iosAppName, isReplace } = { timeout: 3000, ...opt };
    if (env.isApp) {
        const appName = env.isIos ? iosAppName : androidAppName;
        let hasApp = false;
        if (appName) {
            hasApp = await isAppExist(iosAppName, androidAppName);
        }

        if (url) {
            // 存在app, 直接唤起
            if (hasApp) {
                window.location.href = url;
                return 'app';
            }
            // 由于iOS原生插件不能准确检测是否安装app，需尝试唤起app
            let isOpenApp = false;
            if (env.isIos) {
                isOpenApp = await tryCallApp(url, '', timeout, isReplace);
            }
            if (isOpenApp) return 'app';
        }

        // 小程序兜底
        //是否需要尝试拉起小程序
        const isMiniFirst = miniConf && miniConf.miniprogramFirst !== 0;
        if (isMiniFirst) {
            const isOpenMini = await tryCallMiniProgram(miniConf);
            if (isOpenMini) return 'app_mini';
        }

        // h5兜底
        gotoPage(h5url, isReplace);
        return isMiniFirst ? 'app_mini_h5' : 'app_h5';
    } else if (blockedEnv.some(key => env[key])) {
        gotoPage(h5url, isReplace);
        return 'h5';
    } else {
        // 目前微信环境不会唤起小程序
        const res = await tryCallApp(url, h5url, timeout, isReplace);
        return res ? 'app' : 'app_h5';
    }
}
/**
 * 小程序兜底
 * @param {*} opt
 * @param {*} opt.loadingTxt loading文案
 * @param {*} opt.sourceId 小程序原始id
 * @param {*} opt.path 小程序路径
 * @param {*} isReplace
 */

export async function tryCallMiniProgram(opt) {
    if (opt) {
        opt.cb && opt.cb(opt.loadingTxt);
        const res = await openWxMiniProgram({
            appId: opt.sourceId,
            path: opt.path,
        });
        opt.cb && opt.cb('');
        return res;
    } else {
        return false;
    }
}

/**
 * 打开微信小程序（自动识别域名打开对应版本）
 * @param {*} appId 原始id
 * @param {*} path 小程序路径
 */
export function openWxMiniProgram({ appId, path }) {
    return new Promise(resolve => {
        if (!path) resolve(false);
        if ((env.isApp && compareVersion(getAppVersion(), '1.25.0') < 0) || !env.isApp || !path) {
            resolve(false);
            return;
        }

        setTimeout(() => {
            window.zzc.call('openMiniProgram', {
                appId,
                path,
                platform: '1',
                type: '0',
                complete: function(res) {
                    if (res.state === 0) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                },
            });
            if (env.isAndroid) {
                // android没有失败回调，如果3秒内没有打开就说明失败
                const timeId = setTimeout(() => {
                    off();
                    resolve(false);
                }, 2000);
                // 监听页面状态，如果3秒内被隐藏，说明唤醒了app，就清除定时器
                const off = onVisibilityChange(isShow => {
                    if (!isShow) clearTimeout(timeId);
                    off();
                    resolve(true);
                });
            }
        }, 500);
    });
}

/**
 * @param {*} type 事件类型
 * @param {*} params 自定义参数
 * @ 文档 http://bi-data.int.black-unique.com/prod/yd/index.html#/event/info?event_id=1543
 */
const track = params => {
    if (!window.bigTj || typeof window.bigTj.track !== 'function') return;

    window.bigTj.track('performance', {
        obj_id: 'cps_jump',
        obj_type: 'cps_jump',
        item_platform: '', // jd;pdd;vip;sn;kl 平台缩写代表业务对应的平台
        item_terminal: '', // mini_program 代表该平台跳转的是微信小程序 app 代表该平台跳转的是APP h5 代表该平台跳转的是H5
        item_type: '', // goods表示是商品的跳转；promotion表示是非商品跳转的
        item_id: '', // 从商品跳转的上报商品ID，从中转页跳转的，上报中转页配置ID
        item_result: '', // succes表示跳转成功；fail表示跳转失败
        item_name: '',
        item_sort: '',
        location: '',
        location_id: '',
        location_sort: '',
        sa_card_id: '',
        ...params,
    });
};

/**
 * 梳理唤起第三方页面流程并上报
 * @param {*} result 打开过程，以下划线（_）分割
 * @param {*} params 自定义参数
 */
export const handleCallAppTrack = (result, params) => {
    if (!result) return;
    const list = result.split('_');
    const len = list.length;
    list.forEach((terminal, index) => {
        const item_terminal = terminal === 'mini' ? 'mini_program' : terminal;
        track({
            item_terminal,
            item_result: len === index + 1 ? 'succes' : 'fail', // 最后一个代表成功
            ...params,
        });
    });
};
